import { SendPlusFill, ThreeDotsVertical } from "react-bootstrap-icons";
import TableHeader from "../Components/TableHeader";
import OutsideClickHandler from "react-outside-click-handler";
import TableLoader from "../Components/TableLoader";
import TableFoot from "../Components/TableFoot";
import DialogModal from "../Components/DialogModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import AddNewAlert from "../Components/AddNewAlert";
import api from "../lib/api";
import useNotify from "../hooks/useNotify";
import useListData from "../hooks/useListData";
import useTableFilter from "../hooks/useTableFilter";
import useSortOptions from "../hooks/useSortOptions";

const EMPTY_FORM = { title: "", html: "", alert_type: "" };

const Alerts = ({ setNotification }) => {
    const notify = useNotify(setNotification);
    const { filter, setFilter, clampPage } = useTableFilter({
        page: 1, limit: 10, keyword: "", sort_by: "id", sort_order: "DESC",
    });

    const [active, setActive] = useState("");
    const [dialog, setDialog] = useState({ title: "", text: "", show: false, id: "", loader: false });
    const [modals, setModals] = useState({ createAlert: false, deleteAlert: false });
    const [action, setAction] = useState("add");
    const [formData, setFormData] = useState(EMPTY_FORM);

    const sortBy = useSortOptions("accounts-sort-by", notify);
    const onListError = useCallback((err) => notify.error(err), [notify]);
    const { data: alerts, total, loading, reload } = useListData("/admin-access/alerts", filter, { onError: onListError });

    // `total` is a record count; TableFoot needs a page count. This page used
    // to pass the raw record count straight through, so 500 alerts rendered a
    // 500-page pagination bar.
    const pageCount = useMemo(() => Math.max(1, Math.ceil(total / filter.limit)), [total, filter.limit]);
    useEffect(() => { clampPage(total); }, [total, clampPage]);

    const handlers = {
        addNewHandler() {
            // Reset both the form and the mode. Without this, editing an alert
            // and then clicking "Add New" reopened the modal pre-filled and
            // submitted a PUT against the edited record.
            setFormData(EMPTY_FORM);
            setAction("add");
            setModals((prev) => ({ ...prev, createAlert: true }));
        },
        openEditAlertModal(alert) {
            setActive("");
            setFormData({ title: alert.title, html: alert.html, alert_type: alert.alert_type, id: alert.id });
            setAction("edit");
            setModals((prev) => ({ ...prev, createAlert: true }));
        },
        openDeleteAlertModal(alert) {
            setDialog({ show: true, title: "Delete alert", text: "Do you want to remove this alert?", id: alert.id, loader: false });
            setActive("");
        },
        removeAlert() {
            setDialog((prev) => ({ ...prev, loader: true }));
            api.delete(`/alerts/${dialog.id}`)
                .then((res) => {
                    notify.success(res.data.message.en);
                    reload();
                })
                // Was reporting failures with type "success".
                .catch((err) => notify.error(err))
                .finally(() => setDialog({ show: false, title: "", text: "", id: "", loader: false }));
        },
    };

    return (
        <div className="page-content-container">
            <h2 className="page-title">Alerts</h2>
            <TableHeader
                addNewHandler={handlers.addNewHandler}
                setNotification={setNotification}
                AddNewButtonIcon={SendPlusFill}
                setModals={setModals}
                filter={filter}
                sortBy={sortBy}
                exportButton={false}
                checkboxes={[]}
                setFilter={setFilter}
            />
            <div className="table-scroll">
                <table className="responsive-d-table">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Message</th>
                            <th>Type</th>
                            <th><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!loading && alerts.length > 0) && alerts.map((alrt, id) => (
                            <tr key={alrt.id} className={(id + 2) % 2 === 0 ? "even-row" : "odd-row"}>
                                <td>{alrt.title}</td>
                                {/* Guarded: a null `html` column threw on .slice. */}
                                <td title={alrt.html || ""}>{(alrt.html || "").slice(0, 50)}</td>
                                <td><div className="alert-type-box" style={{ background: alrt.alert_type }} title={alrt.alert_type}></div></td>
                                <td>
                                    <button
                                        type="button"
                                        className="row-actions-trigger"
                                        aria-label={`Actions for ${alrt.title}`}
                                        onClick={() => setActive((prev) => (prev === alrt.id ? "" : alrt.id))}
                                    >
                                        <ThreeDotsVertical />
                                    </button>
                                    {active === alrt.id && <OutsideClickHandler onOutsideClick={() => setActive("")}>
                                        <div className="table-actions-box">
                                            <ul>
                                                <li onClick={() => handlers.openEditAlertModal(alrt)}>Edit</li>
                                                <li onClick={() => handlers.openDeleteAlertModal(alrt)}>Delete</li>
                                            </ul>
                                        </div>
                                    </OutsideClickHandler>}
                                </td>
                            </tr>
                        ))}
                        {(!loading && alerts.length === 0) && <tr className="empty-row">
                            <td colSpan={4}><div className="empty-records"><span>Records not found</span></div></td>
                        </tr>}
                        {loading && <TableLoader colspan={4} />}
                    </tbody>
                </table>
            </div>
            <TableFoot filter={filter} setFilter={setFilter} count={pageCount} total={total} />
            <DialogModal
                show={dialog.show}
                setShow={(value) => setDialog((prev) => ({ ...prev, show: value }))}
                title={dialog.title}
                text={dialog.text}
                onSubmit={handlers.removeAlert}
                loader={dialog.loader}
            />
            <AddNewAlert
                show={modals.createAlert}
                setShow={(value) => setModals((prev) => ({ ...prev, createAlert: value }))}
                formData={formData}
                action={action}
                setFormData={setFormData}
                setNotification={setNotification}
                onSaved={reload}
            />
        </div>
    )
}

export default Alerts
